import { Button, CircularProgress, Grid } from '@mui/material';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import CustomSelectField from '../formFields/selectField';
import { paymentMethodArray, showErrorTost, showSuccessTost, statusOptions } from 'src/constants';
import LoaderButton from '../loaderButton/loaderButton';
import { GET_SETTING, GET_SUBORDER, UPDATE_ORDER } from 'src/apiService/apiDeclaration';
import { useSelector } from 'react-redux';
import PageHeader from '../pageHeader/PageHeader';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Invoice from '../pdfs/Invoice';
import { useModal } from 'src/hooks/useModal';
import { yupResolver } from '@hookform/resolvers/yup';
import { updateOrderSchema } from 'src/config/yup';
import SubOrder from './SubOrder';
import CustomTextField from '../formFields/textField';
import TabTitle from '../tabTitle/tabTitle';
import { USER_ROLES } from 'src/config/enum';
import BanksModal from '../common/BanksModal';

const OrderForm = ({ state, setState, onUpdateOrder, customerInfo }) => {
  // Render counter for debugging
  const renderCount = React.useRef(0);
  renderCount.current += 1;

  console.log('🔄 OrderForm component rendering - Render #', renderCount.current);
  console.log('📊 Props received:', {
    stateId: state?._id,
    orderNumber: state?.orderNumber,
    subOrderCount: state?.subOrder?.length,
    customerId: customerInfo?._id,
  });

  const { handleModalClose, setDialogData } = useModal();
  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const [subOrder, setSubOrder] = useState([]);
  const [enableBtn, setEnableBtn] = useState(false);
  const [settings, setSettings] = useState(null);

  // Memoize form default values to prevent recalculation on every render
  const defaultValues = useMemo(
    () => ({
      orderId: state._id,
      orderNumber: state.orderNumber,
      customerId: customerInfo?._id,
      status: state.status?.length > 0 ? state.status[state.status.length - 1].status : '',
      paymentMethod: state.paymentMethod ? state.paymentMethod : 'default',
      subOrder: [],
      phoneNumber: customerInfo?.phoneNumber,
      providerId: 14,
    }),
    [state._id, state.orderNumber, state.status, state.paymentMethod, customerInfo?._id, customerInfo?.phoneNumber]
  );

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(updateOrderSchema),
    defaultValues,
  });

  // Memoize functions to prevent recreation on every render
  const getSubOrders = useCallback(async () => {
    if (subLoading) return; // Prevent multiple simultaneous calls

    console.log('📥 getSubOrders called');
    setSubLoading(true);
    try {
      let { data } = await GET_SUBORDER(state?._id);
      console.log('📦 Sub orders fetched:', data.length, 'items');
      setState((prevState) => ({ ...prevState, subOrder: data }));
      setSubOrder(data); // Update local subOrder state
    } catch (error) {
      console.error('Error fetching sub orders:', error);
    } finally {
      setSubLoading(false);
    }
  }, [state?._id, subLoading, setState]);

  const getOptionsArray = useCallback((currentStatus) => {
    return statusOptions[currentStatus];
  }, []);

  const isSuccessfull = !['PENDING_REVIEW', 'NEGOTIATION_FAILURE'].includes(watch('status'));

  const updateOrder = useCallback(
    async (body) => {
      setLoading(true);
      if (body.paymentMethod !== 'UNIPESA') {
        delete body.phoneNumber;
        delete body.providerId;
      }
      if (!isSuccessfull) {
        delete body.paymentMethod;
        subOrder.forEach((sub) => {
          delete sub.perCubicMeterConcretePrice;
          delete sub.status;
        });
        body = { ...body, subOrder: subOrder };
      } else {
        subOrder.forEach((sub) => {
          sub.perCubicMeterConcretePrice = Number(sub.perCubicMeterConcretePrice);
          delete sub.status;
        });
        body = {
          ...body,
          subOrder,
          ...(state.totalAmount &&
            state.totalConcrete && { totalAmount: state.totalAmount, totalConcrete: state.totalConcrete }),
        };
      }

      try {
        const resp = await UPDATE_ORDER(state._id, body);
        setState(resp.data);
        onUpdateOrder();
        showSuccessTost(resp.message);
      } catch (error) {
        showErrorTost(error.message);
      } finally {
        setLoading(false);
        getSubOrders();
      }
    },
    [isSuccessfull, subOrder, state._id, state.totalAmount, state.totalConcrete, setState, onUpdateOrder, getSubOrders]
  );

  const onSuccess = useCallback(() => {
    getSubOrders();
    handleModalClose();
  }, [getSubOrders, handleModalClose]);

  const generatePdf = useCallback(() => {
    console.log('🔍 PDF generation started');
    console.log('📊 State data:', state);
    console.log('📋 Order number:', state.orderNumber);

    // Check if required libraries are available
    console.log('📚 jsPDF available:', typeof jsPDF !== 'undefined');
    console.log('🎨 html2canvas available:', typeof html2canvas !== 'undefined');

    if (typeof jsPDF === 'undefined') {
      console.error('❌ jsPDF library not available!');
      return;
    }

    if (typeof html2canvas === 'undefined') {
      console.error('❌ html2canvas library not available!');
      return;
    }

    const input = document.getElementById('invoice');
    console.log('🎯 Invoice element found:', input);

    if (!input) {
      console.error('❌ Invoice element not found! Check if Invoice component is rendered');
      return;
    }

    console.log('📄 Invoice element display before:', input.style.display);
    input.style.display = 'flex';
    console.log('📄 Invoice element display after:', input.style.display);

    console.log('🎨 Starting html2canvas conversion...');
    html2canvas(input, { scale: 2 })
      .then((canvas) => {
        console.log('✅ Canvas created successfully:', canvas);
        console.log('📏 Canvas dimensions:', { width: canvas.width, height: canvas.height });

        const imgData = canvas.toDataURL('image/png');
        console.log('🖼️ Image data generated, length:', imgData.length);

        console.log('📄 Creating PDF...');
        const pdf = new jsPDF('p', 'mm', 'a4');
        console.log('📄 PDF object created:', pdf);

        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297);
        console.log('🖼️ Image added to PDF');

        const fileName = `BillNo_${state.orderNumber}.pdf`;
        console.log('💾 Saving PDF as:', fileName);
        pdf.save(fileName);
        console.log('✅ PDF saved successfully!');
      })
      .catch((error) => {
        console.error('❌ Error in html2canvas:', error);
      });

    input.style.display = 'none';
    console.log('📄 Invoice element hidden again');
  }, [state]);

  const getTaxRate = useCallback(async () => {
    if (settings?.taxRate !== undefined && settings?.taxRate !== null) return; // Don't fetch if already available

    console.log('💰 getTaxRate called');
    try {
      let resp = await GET_SETTING();
      console.log('💵 Tax rate fetched:', resp.data?.taxRate);
      setSettings(resp.data);
    } catch (error) {
      console.error('Error fetching tax rate:', error);
    }
  }, [settings?.taxRate]);

  // Memoize completion check to prevent unnecessary calculations
  const completionCheck = useMemo(() => {
    if (state.subOrder && state.status) {
      const isCompleted = state.subOrder.every((sub) => sub.status[sub.status.length - 1].status === 'DELIVERED');
      const currentStatus = state.status[state.status.length - 1].status;
      return { isCompleted, currentStatus };
    }
    return { isCompleted: false, currentStatus: null };
  }, [state.subOrder, state.status]);

  // Initialize data on component mount - only run once
  useEffect(() => {
    console.log('🔧 useEffect [initializeData] triggered');

    const initializeData = async () => {
      if (!state.subOrder) {
        console.log('📥 Fetching sub orders...');
        await getSubOrders();
      }
      if (settings?.taxRate === undefined || settings?.taxRate === null) {
        console.log('💰 Fetching tax rate...');
        await getTaxRate();
      }
    };

    initializeData();
  }, [getSubOrders, getTaxRate, settings?.taxRate, state.subOrder]);

  // Check if order is completed and update if needed
  useEffect(() => {
    console.log('🔧 useEffect [checkCompletion] triggered');

    const { isCompleted, currentStatus } = completionCheck;

    if (isCompleted && currentStatus && currentStatus !== 'COMPLETED') {
      console.log('🔄 Updating order due to completion...');
      onUpdateOrder();
    }
  }, [completionCheck, onUpdateOrder]);

  // Update form values when subOrder changes - optimized to prevent excessive setValue calls
  useEffect(() => {
    console.log('🔧 useEffect [updateFormValues] triggered');
    console.log('📋 SubOrder changed:', {
      subOrderLength: subOrder.length,
      subOrderIds: subOrder.map((sub) => sub._id || sub.subOrderNumber),
    });

    if (subOrder.length > 0) {
      console.log('📝 Updating form values for', subOrder.length, 'sub orders');

      // Batch setValue calls to reduce re-renders
      const formUpdates = {};
      subOrder.forEach((sub, index) => {
        formUpdates[`subOrder[${index}].dosage`] = sub.dosage;
        formUpdates[`subOrder[${index}].volumeOfConcrete`] = sub.volumeOfConcrete;
        formUpdates[`subOrder[${index}].perCubicMeterConcretePrice`] = sub.perCubicMeterConcretePrice
          ? sub.perCubicMeterConcretePrice
          : 0;
        formUpdates[`subOrder[${index}].deliveryDate`] = sub.deliveryDate;
        formUpdates[`subOrder[${index}].timeOfDelivery`] = sub.timeOfDelivery;
        formUpdates[`subOrder[${index}].streetNumber`] = sub.streetNumber;
        formUpdates[`subOrder[${index}].streetAddress`] = sub.streetAddress;
        formUpdates[`subOrder[${index}].municipality`] = sub.municipality;
        formUpdates[`subOrder[${index}].city`] = sub.city;
        formUpdates[`subOrder[${index}].country`] = sub.country;
        formUpdates[`subOrder[${index}].status`] = sub.status[sub.status.length - 1].status;
      });

      // Apply all updates at once
      Object.entries(formUpdates).forEach(([field, value]) => {
        setValue(field, value, { shouldValidate: false, shouldDirty: false });
      });

      const enableBtn = subOrder.every((sub) => sub.perCubicMeterConcretePrice > 0);
      console.log('🔘 Update button enabled:', enableBtn);
      setEnableBtn(enableBtn);
    }
  }, [subOrder, setValue]);

  return (
    <>
      <TabTitle title={'Order Details'} />
      <form onSubmit={handleSubmit(updateOrder)}>
        <Grid item xs={12}>
          <PageHeader pageTitle={t('orderDetailsPage.heading') + state.orderNumber} />
          <Grid xs={12} container sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginY: 2 }}>
            {user.role !== USER_ROLES.OPERATOR && (
              <>
                <Grid item sx={{ display: 'flex', gap: 2 }} xs={12} sm={2}>
                  <LoaderButton
                    disabled={
                      subLoading ||
                      state.status[state.status.length - 1].status === 'PENDING_REVIEW' ||
                      settings?.taxRate === undefined ||
                      settings?.taxRate === null
                    }
                    buttonText={t('common.download') + ' ' + t('invoice.bill')}
                    onClick={() => {
                      console.log('🖱️ Download button clicked');
                      console.log(
                        '🔒 Button disabled state:',
                        subLoading ||
                          state.status[state.status.length - 1].status === 'PENDING_REVIEW' ||
                          settings?.taxRate === undefined ||
                          settings?.taxRate === null
                      );
                      console.log('📊 Sub loading:', subLoading);
                      console.log('📋 Current status:', state.status[state.status.length - 1].status);
                      console.log(
                        '💰 Tax rate available:',
                        settings?.taxRate !== undefined && settings?.taxRate !== null
                      );
                      generatePdf();
                    }}
                  />
                  {state.subOrder?.length > 0 && <Invoice data={{ ...state, taxRate: settings?.taxRate }} />}
                </Grid>
              </>
            )}
            {user.role !== USER_ROLES.CUSTOMER && user.role !== USER_ROLES.OPERATOR && (
              <Grid item xs={12} sm={2} sx={{ display: 'flex', gap: 2 }}>
                <LoaderButton
                  buttonText={t('common.update')}
                  type="submit"
                  loading={loading}
                  disabled={!isValid || (isSuccessfull && !enableBtn) || !isDirty}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={4}>
          {user.role !== USER_ROLES.CUSTOMER && (
            <>
              <Grid item xs={12} sm={6}>
                <CustomSelectField
                  label={t('orderDetailsPage.orderStatus')}
                  optionsArray={getOptionsArray(state?.status[state.status.length - 1]?.status)}
                  disabled={user.role === USER_ROLES.OPERATOR}
                  controller={{
                    name: 'status',
                    control: control,
                    errors: errors.status,
                  }}
                />
              </Grid>
              {isSuccessfull && (
                <Grid item xs={12} sm={6}>
                  <CustomSelectField
                    label={t('customerPage.paymentMethod')}
                    optionsArray={paymentMethodArray}
                    disabled={user.role === USER_ROLES.OPERATOR || watch('status') !== 'NEGOTIATION_SUCCESSFUL'}
                    controller={{
                      name: 'paymentMethod',
                      control: control,
                      errors: errors.paymentMethod,
                    }}
                  />
                  {watch('paymentMethod') === 'BANK_ACCOUNT' && (
                    <Button
                      mt={2}
                      onClick={() =>
                        setDialogData({
                          showModal: true,
                          title: t('bankDetails.title'),
                          child: <BanksModal />,
                        })
                      }
                      disabled={!settings}
                    >
                      {t('orderDetailsPage.viewBankDetails')}
                    </Button>
                  )}
                </Grid>
              )}
            </>
          )}
          {user.role === USER_ROLES.CUSTOMER && watch('paymentMethod') === 'BANK_ACCOUNT' && (
            <Grid item>
              <Button
                mt={2}
                onClick={() =>
                  setDialogData({
                    showModal: true,
                    title: t('bankDetails.title'),
                    child: <BanksModal />,
                  })
                }
                disabled={!settings}
              >
                {t('orderDetailsPage.viewBankDetails')}
              </Button>
            </Grid>
          )}
          {watch('paymentMethod') === 'UNIPESA' && (
            <Grid item xs={12} sm={6}>
              <CustomTextField
                label={t('common.phone')}
                controller={{
                  name: 'phoneNumber',
                  control: control,
                  errors: errors.phoneNumber,
                }}
              />
            </Grid>
          )}
        </Grid>
      </form>
      <Grid container xs={12}>
        {subLoading || state.subOrder?.length === 0 ? (
          <Grid
            container
            xs={12}
            sx={{ minHeight: '12rem', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <CircularProgress />
          </Grid>
        ) : (
          state.subOrder?.map((order, index) => {
            return (
              <SubOrder
                order={{
                  ...order,
                  orderNumber: state.orderNumber,
                  orderId: state._id,
                }}
                subOrder={subOrder}
                setSubOrder={setSubOrder}
                isSuccessfull={isSuccessfull}
                key={index}
                customerInfo={customerInfo}
                onSuccess={onSuccess}
                onUpdateOrder={onUpdateOrder}
                currentStatus={state.status[state.status.length - 1].status}
              />
            );
          })
        )}
      </Grid>
    </>
  );
};

// Memoize the component to prevent unnecessary re-renders
export default React.memo(OrderForm, (prevProps, nextProps) => {
  // Custom comparison function to determine if re-render is needed
  return (
    prevProps.state._id === nextProps.state._id &&
    prevProps.state.orderNumber === nextProps.state.orderNumber &&
    prevProps.state.status?.length === nextProps.state.status?.length &&
    prevProps.state.subOrder?.length === nextProps.state.subOrder?.length &&
    prevProps.customerInfo?._id === nextProps.customerInfo?._id &&
    prevProps.onUpdateOrder === nextProps.onUpdateOrder
  );
});
